import { reaction } from 'mobx';
import { useEffect, useRef, useState } from 'react';

/**
 * Converts a Mobx Observable field into a standard React object.
 * @param selector wrapped mobx observable
 */
export function useObservation<T>(selector: () => T): T;
/**
 * Converts a Mobx Observable field into a standard React object.
 * @param selector wrapped mobx observable
 * @param conversion function to run against observations, for converting observable to a new object type. should ideally have referential stability.
 */
export function useObservation<T, R>(selector: () => T, conversion: (obs: T) => R): R;
export function useObservation<T, R>(selector: () => T, conversion: (obs: T) => T | R = (obs: T) => obs): T | R {
  // use refs to prevent re-rendering based solely on changes to selector or conversion functions
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const conversionRef = useRef(conversion);
  conversionRef.current = conversion;

  const [result, setResult] = useState<T | R>(() => conversion(selectorRef.current()));

  useEffect(
    () =>
      reaction(
        () => selectorRef.current(),
        (newValue: T) => {
          setResult(conversionRef.current(newValue));
        }
      ),
    []
  );

  return result;
}
